import { useMutation } from '@tanstack/react-query';
import { Avatar, Col, Row, Tag } from 'antd';
import { HTMLAttributes, useEffect, useState } from 'react';
import { PerkRequestStatus } from '../../enums';
import perksService from '../../services/perks.service';
import { notifyError, notifySuccess } from '../../utils/notification';
import { PerkModal } from '../perk-modal';
import { ButtonType, TAButton } from '../widgets';
import { ListableCardContainer } from '../widgets/listable-card';
import { useTheme } from 'styled-components';

export interface IPerkCardProps {
	id: string;
	logo: string;
	providerName: string;
	title: string;
	description: string;
	ctaLink?: string;
	promoCode?: string;
	percent?: number;
	requestStatus: PerkRequestStatus;
	tags?: { name: string; color: string }[];
}

export const PerkCard: React.FC<HTMLAttributes<HTMLDivElement> & IPerkCardProps> = ({
	id,
	description,
	logo,
	providerName,
	title,
	percent,
	ctaLink,
	promoCode,
	requestStatus,
	tags,
	...restProps
}) => {
	const theme = useTheme();
	const [perkModalVisible, setPerkModalVisible] = useState<boolean>(false);
	const [perkRequestStatus, setPerkRequestStatus] = useState<PerkRequestStatus>();
	const { mutate: requestPerkMutation, isLoading: isRequesting } = useMutation({
		mutationKey: ['request-perk'],
		mutationFn: (perkId: string) => perksService.requestPerk(perkId),
		onSuccess() {
			setPerkRequestStatus(PerkRequestStatus.PENDING);
			notifySuccess('Perk requested successfully');
		},
		onError(error: any) {
			notifyError(error.message);
		}
	});
	useEffect(() => {
		setPerkRequestStatus(requestStatus);
	}, [requestStatus]);

	const onGetPerk = () => {
		if (!(promoCode || ctaLink)) requestPerkMutation(id);
		else setPerkModalVisible(true);
	};

	const perkRequestStatusColorMapper = {
		[PerkRequestStatus.PENDING]: 'orange',
		[PerkRequestStatus.COMPLETED]: 'green',
		[PerkRequestStatus.CANCELLED]: 'red'
	};

	return (
		<Row {...restProps}>
			<Col xs={24} lg={16} xl={15} xxl={12}>
				<ListableCardContainer className="perk-card w-100" style={{ backgroundColor: theme.CARDS_BG }}>
					<Col xs={6} md={2}>
						<img src={logo} style={{ width: '50px' }} />
					</Col>
					<Col xs={12} md={16}>
						<span className="d-inline-block mb-2">{providerName}</span>
						<h5>{title}</h5>
						<p>{description}</p>

						{perkRequestStatus ? (
							<Tag color={perkRequestStatusColorMapper[perkRequestStatus]}>{perkRequestStatus}</Tag>
						) : (
							<TAButton btntype={ButtonType.PRIMARY} onClick={onGetPerk} loading={isRequesting}>
								Get Perk
							</TAButton>
						)}
					</Col>

					<Col xs={6} className="d-flex flex-column justify-content-between">
						{percent ? (
							<div className="d-flex align-items-center justify-content-end">
								<h3 className="m-0 p-0 me-2">{percent}%</h3>
								<span style={{ fontSize: '1.4rem' }}>Off</span>
							</div>
						) : (
							false
						)}

						{tags?.length ? (
							<div className="d-flex flex-row flex-wrap align-items-end">
								{tags.map(tag => (
									<Tag className="m-1" key={tag.name} color={tag.color}>
										{tag.name}
									</Tag>
								))}
							</div>
						) : (
							false
						)}
					</Col>
				</ListableCardContainer>
				{ctaLink ? (
					<PerkModal
						open={perkModalVisible}
						logo={logo}
						title={title}
						percent={percent}
						ctaLink={ctaLink}
						promoCode={promoCode}
						onCancel={() => setPerkModalVisible(false)}
					/>
				) : (
					false
				)}
			</Col>
		</Row>
	);
};
